<script setup lang="ts">
import { computed, ref } from 'vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import type { FileDropAction } from '@/components/file-dropper/actions/types.ts';
import { startReviewAction } from '@/components/file-dropper/actions/startReviewAction.ts';
import { createContractAction } from '@/components/file-dropper/actions/createContractAction.ts';
import { signDocumentAction } from '@/components/file-dropper/actions/signDocumentAction.ts';
import { playbookAction } from '@/components/file-dropper/actions/playbookAction.ts';

/**
 * The home page's quick-action row. Each button runs the same flow the file
 * dropper runs for that action — the only difference is where the files come
 * from: the dropper gets them from a drag, these get them from a file picker.
 * Reusing the shared action objects keeps one definition of each action's
 * label, icon, permission gate and flow component.
 */
interface QuickAction {
  action: FileDropAction;
  /** Whether the picker accepts more than one file. */
  multiple: boolean;
}

const QUICK_ACTIONS: QuickAction[] = [
  { action: startReviewAction, multiple: true },
  { action: createContractAction, multiple: true },
  { action: signDocumentAction, multiple: true },
  { action: playbookAction, multiple: true },
];

// `isAvailable` is defined over the files an action would receive, so the
// question to ask before anything is picked is "would this be offered for a
// single-file pick?" — the smallest selection every action accepts.
const availableActions = computed(() => {
  const probe = [new File([], 'probe')];
  return QUICK_ACTIONS.filter(({ action }) => action.isAvailable?.(probe) ?? true);
});

/**
 * Two up on a narrow window, one column per action above naive's `s`. Still a
 * column per action in the FULL set rather than per available one, so a button
 * is the same width for everyone — permission gating leaves the trailing cell
 * empty rather than stretching the buttons that remain.
 */
const gridCols = `2 s:${QUICK_ACTIONS.length}`;

const fileInput = ref<HTMLInputElement | null>(null);
const pendingAction = ref<FileDropAction | null>(null);
const activeAction = ref<FileDropAction | null>(null);
const pickedFiles = ref<File[]>([]);

function startAction(entry: QuickAction): void {
  const input = fileInput.value;
  if (!input) return;
  pendingAction.value = entry.action;
  input.multiple = entry.multiple;
  // Clear first, so picking the same file twice in a row still fires `change`.
  input.value = '';
  input.click();
}

function onFilesPicked(event: Event): void {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  const action = pendingAction.value;
  pendingAction.value = null;
  // Cancelling the picker fires no change event, but an empty pick would.
  if (!action || !files.length) return;
  // Simple actions run straight away; the rest mount their own flow below.
  if (action.run) {
    void action.run(files, { close: reset });
    return;
  }
  pickedFiles.value = files;
  activeAction.value = action;
}

function reset(): void {
  activeAction.value = null;
  pickedFiles.value = [];
}
</script>

<template>
  <n-grid :cols="gridCols" responsive="screen" :x-gap="8" :y-gap="10" data-tour="home.workflows">
    <n-grid-item v-for="entry in availableActions" :key="entry.action.key">
      <gaia-button block size="large" @click="startAction(entry)">
        <template v-if="entry.action.icon" #icon>
          <n-icon :component="entry.action.icon" />
        </template>
        {{ entry.action.label }}
      </gaia-button>
    </n-grid-item>
  </n-grid>

  <!-- Hidden trigger — the quick-action buttons above are the real controls. -->
  <input
    ref="fileInput"
    type="file"
    aria-label="Choose files for the selected quick action"
    style="display: none"
    @change="onFilesPicked"
  />

  <!-- The chosen action's own flow — the same component the dropper mounts. -->
  <component
    :is="activeAction.component"
    v-if="activeAction?.component"
    :files="pickedFiles"
    @done="reset"
  />
</template>
